import {
  ref, computed, Ref, watch,
} from 'vue';
import { resolveConfidenceThreshold } from 'dive-common/typeHierarchy';
import type { AnnotationId, ConfidencePair } from './BaseAnnotation';
import { SortedAnnotation } from './BaseAnnotationStore';
import type Group from './Group';
import type Track from './track';
import { updateSubset } from './utils';
import { AttributeTrackFilter } from './AttributeTrackFilterControls';

interface MarkChangesPendingData {
  action?: 'meta' | 'upsert' | 'delete';
  attributeTrackFilter?: AttributeTrackFilter;
}
export type MarkChangesPendingFilter = (data?: MarkChangesPendingData) => void;

export const DefaultConfidence = 0.1;

/** Which annotations a delete-all reaches, relative to each type's threshold. */
export type ThresholdScope = 'above' | 'below' | 'all';
/**
 * AnnotationWithContext wraps an annotation with additional information
 * such as why the annotation was included or returned by a system
 * or function.
 */
export interface AnnotationWithContext<T> {
  annotation: Readonly<SortedAnnotation<T>>;
  context: {
    // confidencePair index within annotation that makes this annotation a positive filter result
    confidencePairIndex: number;
  };
}

export interface FilterControlsParams<T extends Track | Group> {
  sorted: Ref<SortedAnnotation<T>[]>;
  markChangesPending: MarkChangesPendingFilter;
  remove: (id: AnnotationId) => void;
  removeTypes: (id: AnnotationId, types: string[]) => ConfidencePair[];
  getTrack?: (trackId: Readonly<AnnotationId>, cameraName?: string) => T;
}

export type TrackWithContext = AnnotationWithContext<Track>;
export type GroupWithContext = AnnotationWithContext<Group>;

/* Provide annotation filtering controls on annotations loaded from store. */
export default abstract class BaseFilterControls<T extends Track | Group> {
  /* Annotation IDs explicitly checked "ON" by the user */
  checkedIDs: Ref<AnnotationId[]>;

  /* The confidence threshold to test confidecePairs against */
  confidenceFilters: Ref<Record<string, number>>;

  /* Time filtering values */
  timeFilters: Ref<[number, number] | null>;

  /* The types informed by explicit meta configuration */
  configuredTypes: Ref<string[]>;

  /* Collect all known types from confidence pairs */
  allTypes: Ref<string[]>;

  /* Types currently assigned to at least one annotation */
  usedTypes: Ref<string[]>;

  /* Types that should be persisted through type/style configuration */
  usedPlusConfiguredTypes: Ref<string[]>;

  /* Categorical types checked "ON" by the user */
  checkedTypes: Ref<string[]>;

  /* Annotation IDs filtered by type and confidence threshold */
  filteredAnnotations: Ref<AnnotationWithContext<T>[]>;

  /* AnnotationIDs further filtered by individual checkedIds */
  enabledAnnotations: Ref<AnnotationWithContext<T>[]>;

  /* MarkChangesPending is called when meta config default types are modified  */
  markChangesPending: MarkChangesPendingFilter;

  /* Hold a reference to the annotationStore */
  sorted: Readonly<Ref<SortedAnnotation<T>[]>>;

  remove: (id: AnnotationId) => void;

  removeTypes: (id: AnnotationId, types: string[]) => ConfidencePair[];

  disableAnnotationFilters: Ref<boolean>;

  constructor(params: FilterControlsParams<T>) {
    this.checkedIDs = ref(params.sorted.value.map((t) => t.id));

    this.confidenceFilters = ref({ default: DefaultConfidence } as Record<string, number>);

    this.timeFilters = ref(null);

    this.configuredTypes = ref([]);

    this.sorted = params.sorted;

    this.remove = params.remove;

    this.removeTypes = params.removeTypes;

    this.markChangesPending = params.markChangesPending;

    this.disableAnnotationFilters = ref(false);

    this.usedTypes = computed(() => {
      const typeSet = new Set<string>();
      this.sorted.value.forEach((annotation) => {
        annotation.confidencePairs.forEach(([name]) => {
          typeSet.add(name);
        });
      });
      return Array.from(typeSet);
    });

    this.usedPlusConfiguredTypes = computed(() => {
      const typeSet = new Set(this.usedTypes.value);
      this.configuredTypes.value.forEach((type) => {
        typeSet.add(type);
      });
      return Array.from(typeSet);
    });

    this.allTypes = this.usedPlusConfiguredTypes;

    this.checkedTypes = ref(Array.from(this.allTypes.value));

    this.filteredAnnotations = ref([]);

    this.enabledAnnotations = computed(() => {
      const checkedSet = new Set(this.checkedIDs.value);
      return this.filteredAnnotations.value
        .filter((filtered) => checkedSet.has(filtered.annotation.id));
    });

    // because vue watchers don't behave properly, and it's better to not have
    // checkedIDs be a union null | array type
    let oldCheckedIds: AnnotationId[] = [];
    /* When the list of types (or checked IDs) changes
    * add the new enabled types to the set and remove old ones */
    watch(params.sorted, (newval) => {
      const IDs = newval.map((t) => t.id);
      const newArr = updateSubset(oldCheckedIds, IDs, this.checkedIDs.value);
      if (newArr !== null) {
        oldCheckedIds = IDs;
        this.checkedIDs.value = newArr;
      }
    });

    let oldCheckedtypes: string[] = [];
    watch(this.usedTypes, (newval) => {
      const newArr = updateSubset(oldCheckedtypes, newval, this.checkedTypes.value);
      if (newArr !== null) {
        oldCheckedtypes = Array.from(newval);
        this.checkedTypes.value = newArr;
      }
    });
  }

  importTypes(types: string[], userInteraction = true) {
    types.forEach((type) => {
      if (!this.configuredTypes.value.includes(type)) {
        this.configuredTypes.value.push(type);
      }
    });
    if (userInteraction) {
      this.markChangesPending({ action: 'meta' });
    }
  }

  /**
   * Make `types` the whole declared list, as a freshly loaded dataset states it. importTypes
   * only ever grows the list, so a reload after an Overwrite import would keep listing the
   * types the import dropped, and the next save would write them straight back. A choice
   * for a type that is no longer listed is dropped with it.
   */
  setConfiguredTypes(types: string[]) {
    this.configuredTypes.value = Array.from(new Set(types));
    const listed = new Set(this.allTypes.value);
    this.checkedTypes.value = this.checkedTypes.value.filter((name) => listed.has(name));
  }

  /**
   * Carry a renamed type's confidence threshold over to its new name, unless
   * the new name already carries one of its own.
   */
  protected carryConfidenceFilter(currentType: string, newType: string) {
    if (!(newType in this.confidenceFilters.value) && currentType in this.confidenceFilters.value) {
      this.setConfidenceFilters({
        ...this.confidenceFilters.value,
        [newType]: this.confidenceFilters.value[currentType],
      });
    }
  }

  protected deleteTypeConfiguration(type: string) {
    if (this.configuredTypes.value.includes(type)) {
      this.configuredTypes.value.splice(this.configuredTypes.value.indexOf(type), 1);
    }
    delete this.confidenceFilters.value[type];
  }

  deleteType(type: string): boolean {
    this.deleteTypeConfiguration(type);
    this.markChangesPending({ action: 'meta' });
    return true;
  }

  setConfidenceFilters(val?: Record<string, number>) {
    if (val) {
      this.confidenceFilters.value = val;
    }
  }

  setTimeFilters(val: [number, number] | null) {
    this.timeFilters.value = val;
  }

  abstract updateTypeName(params: { currentType: string; newType: string }): void;

  removeTypeAnnotations(types: string[]) {
    const processedIds = new Set<AnnotationId>();
    this.filteredAnnotations.value.forEach((filtered) => {
      if (processedIds.has(filtered.annotation.id)) {
        return;
      }
      processedIds.add(filtered.annotation.id);
      const filteredType = filtered.annotation.getType(filtered.context.confidencePairIndex);
      if (filteredType && types.includes(filteredType)) {
        //Remove the type from the annotation if multiple types exist
        const newConfidencePairs = this.removeTypes(filtered.annotation.id, types);
        if (newConfidencePairs.length === 0) {
          this.remove(filtered.annotation.id);
        }
      }
    });
  }

  /**
   * Tracks with enabled classes, none of which reach their confidence threshold.
   * TrackFilterControls overrides this to also apply time, group, and attribute filters.
   */
  annotationIdsBelowThreshold(types: string[]): AnnotationId[] {
    const wanted = new Set(types);
    const filters = this.confidenceFilters.value;
    return this.sorted.value.filter((annotation) => {
      const matching = annotation.confidencePairs.filter(([type]) => wanted.has(type));
      return matching.length > 0 && matching.every(([type, confidence]) => (
        confidence < resolveConfidenceThreshold(filters, type)
      ));
    }).map(({ id }) => id);
  }

  updateCheckedTypes(types: string[]) {
    this.checkedTypes.value = types;
  }

  updateCheckedId(id: AnnotationId, value: boolean) {
    if (value) {
      this.checkedIDs.value.push(id);
    } else {
      const i = this.checkedIDs.value.indexOf(id);
      this.checkedIDs.value.splice(i, 1);
    }
  }
}
